import { type CommentTargetPayload, ComponentIdentifierEnum } from '@flowx/core-sdk'
import type { HarnessUiContext } from '@flowx/designer/modules/ai-builder/services/harness-ui-context.service'
import { isUuid } from '@flowx/designer/modules/ai-builder/utils/text.util'
import type { RunType } from '@flowx/designer/shared/entities/resource-page.entity'

/** The resource types a run can be commented on from: the pages that offer comment mode. */
export type HarnessCommentResourceType = 'ui-flow' | 'process'

type CommentRunType = Extract<RunType, 'ui-flow-session' | 'process-instance'>

/** The resource that defines the component when that is not the run's own: a reusable template, or
 * a process the page's run only shows (a child instance or an embedded subprocess). */
export type CommentOwner = {
  resource_type: 'reusable-template' | 'process'
  /** The owner's resource definition id. */
  id: string
  /** A process owner's user task node, by its flowxUuid. */
  node_id?: string
  /** A process owner's instance. */
  run_id?: string
}

/** The component the comment is on: its template node's uuid, the template parent's when the node
 * sits in one of the page's own resources, the resource that defines it when that is another, the
 * row it was painted in, and its type when that is a known one. */
export type CommentSelection = {
  type: 'ui-component'
  id: string
  parent_id?: string
  owner?: CommentOwner
  /** The index in the array the collection or table is bound to; the innermost row only. */
  row?: number
  /** A `ComponentIdentifierEnum` value, never a custom component's own name. */
  component_type?: string
}

/**
 * The ui_context of a comment turn: the page's schema-2 context narrowed to the run's UI flow or
 * process as a resource editor with its manifest id, the run, and the one picked component; ids
 * only, no label, name or URL leaves Designer.
 */
export type CommentUiContext = HarnessUiContext & {
  scope: 'app_version'
  app_version_id: string
  page: { type: 'resource_editor'; resource_type: HarnessCommentResourceType; id: string }
  run: { type: CommentRunType; id: string }
  selection: [CommentSelection]
}

/** The run a comment is made on: which resource, and which run of it is on screen. */
export type CommentRun = {
  resourceType: HarnessCommentResourceType
  /** The manifest's resource id, not the run's. */
  resourceId: string
  runId: string
  appVersionId: string
  /** The UI flow's or process's name, for the page the comment's chip names. */
  resourceName: string
  /** A process run's resource definition id, once its definition has loaded: the owner of a
   * component that is the page's own is not named. A UI flow has none to give. */
  resourceDefinitionId?: string
}

const COMPONENT_TYPES: ReadonlySet<string> = new Set(Object.values(ComponentIdentifierEnum))

/** The components that introduce a row: what a row index of a target is the index of. */
const ROW_INTRODUCERS: ReadonlySet<string> = new Set([
  ComponentIdentifierEnum.COLLECTION_PROTOTYPE,
  ComponentIdentifierEnum.TABLE_ROW,
  ComponentIdentifierEnum.TABLE_CELL,
])

/**
 * The row the target sits in: the index, in the array a collection or a table is bound to, of the
 * innermost row it was painted in. A table gives its grid rows the ids 0..n-1 in source order, so
 * its row id is that index. Null when no entry at or above the target introduces a row.
 */
const rowOf = ({ target, hierarchy }: CommentTargetPayload): number | null => {
  const at = hierarchy.findIndex((entry) => entry.flowxUuid === target.flowxUuid)
  const inRow = [target, ...hierarchy.slice(at + 1)].some(({ componentIdentifier }) =>
    ROW_INTRODUCERS.has(componentIdentifier)
  )

  return inRow ? target.rowIndex : null
}

const RUN_TYPE: Readonly<Record<HarnessCommentResourceType, CommentRunType>> = {
  'ui-flow': 'ui-flow-session',
  'process': 'process-instance',
}

/**
 * The first id of the comment that is not a uuid, named by its place in the `ui_context`, or null
 * when they all are. The harness takes uuids only, and a marker the SDK could not resolve can leak
 * a decorated id, so the comment is refused rather than sent pointing at nothing.
 */
export function commentRefusal(picked: CommentTargetPayload, run: CommentRun): string | null {
  const { target } = picked
  const parentId = target.templateParentFlowxUuid
  const ids: ReadonlyArray<readonly [string, string | null | undefined]> = [
    ['selection.id', target.originalFlowxUuid],
    ...(parentId ? [['selection.parent_id', parentId] as const] : []),
    ['page.id', run.resourceId],
    ['run.id', run.runId],
    ['app_version_id', run.appVersionId],
  ]
  const invalid = ids.find(([, id]) => !id || !isUuid(id))

  return invalid ? `${invalid[0]} is not a uuid` : null
}

/** The owner as the harness takes it: an id that is not a uuid leaves no owner, one of its other
 * ids that is not leaves it out of the owner. */
const sendableOwner = (owner: CommentOwner | null): CommentOwner | null =>
  owner && isUuid(owner.id)
    ? {
        resource_type: owner.resource_type,
        id: owner.id,
        ...(owner.node_id && isUuid(owner.node_id) && { node_id: owner.node_id }),
        ...(owner.run_id && isUuid(owner.run_id) && { run_id: owner.run_id }),
      }
    : null

/**
 * The `ui_context` (v2) that tells the harness which component of which run a comment is on, and
 * which resource defines it when that is not the page's (`owner`, see `CommentOwnerService`). The
 * comment text is the turn's prompt and never goes in here; the context carries ids and nothing a
 * maker typed or a screen showed. A comment with an id that is not a uuid is refused
 * (`commentRefusal`); an owner that has none is left out, the comment still goes.
 */
export function toCommentUiContext(
  picked: CommentTargetPayload,
  run: CommentRun,
  owner: CommentOwner | null
): { uiContext: CommentUiContext } | { refused: string } {
  const refusal = commentRefusal(picked, run)

  if (refusal) {
    return { refused: refusal }
  }

  const { target } = picked
  const sendable = sendableOwner(owner)
  // The parent of the root of another resource's screen is in the page's resource: not a node of
  // the resource the owner names.
  const parentId = !(sendable && target.parentInOtherResource) && target.templateParentFlowxUuid
  const row = rowOf(picked)
  const selection: CommentSelection = {
    type: 'ui-component',
    id: target.originalFlowxUuid,
    ...(parentId && { parent_id: parentId }),
    ...(sendable && { owner: sendable }),
    ...(row !== null && { row }),
    ...(COMPONENT_TYPES.has(target.componentIdentifier) && {
      component_type: target.componentIdentifier,
    }),
  }

  return {
    uiContext: {
      schema_version: 2,
      scope: 'app_version',
      app_version_id: run.appVersionId,
      page: { type: 'resource_editor', resource_type: run.resourceType, id: run.resourceId },
      run: { type: RUN_TYPE[run.resourceType], id: run.runId },
      selection: [selection],
    },
  }
}
